/* eslint-disable camelcase */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import pickby from 'lodash.pickby'
import identity from 'lodash.identity'
import { CookiesProvider } from 'react-cookie'
import Cookies from 'universal-cookie'
import { parse } from 'query-string'
import { v4 as uuid } from 'uuid'
import config from 'config'
import scriptLoader from 'react-async-script-loader'

import FullStoryAnalytics from '@cred/fullstory-analytics'
import ThemeProvider from '@cred/react-theme/ThemeProvider'
import Snowplow from '@cred/react-analytics/Snowplow'
import { populatedGtmDataLayer } from '@cred/react-analytics/utils/data'
import gtm from '@cred/shared/gtm'
import { createLogger, logger } from '@cred/fe-logger'
import WidgetSDKClient, { SDKClientCommunicatorInterface } from '@cred/widget-sdk-client'

import ApplicationErrorBoundary from 'components/error-boundary'
import { extractHostname, getRouteParams } from 'utils/urls'
import {
  getMarketplaceParam,
  getMetaParamsFromQuery,
  getOtherOptionsParam,
  parseMultiVerticalOptions,
  removeEmptyValues,
} from 'utils/data'
import { setInsuranceFormInitialValues } from 'utils/getInsurancePrefillData'
import WidgetContext from 'components/widget-context'
import InitialValuesContext from 'components/initial-values-context'
import { DEFAULT_ACTIVE_TAB } from 'common/constants'
import { CALCULATOR_WITH_INSURANCE_FORM_TYPE } from 'home-insurance/map-widget/const'

import styles from './WidgetApp.module.scss'
import 'purecss/build/pure.css' /* Importing Pure here otherwise the import order gets incorrect. */
import 'purecss/build/grids-responsive.css'
import '@cred/global-assets/styles/common.scss'
import '@cred/global-assets/styles/cred-icon.scss'
import isFeatureEnabled from './utils/featureToggle/isFeatureEnabled'
import { FULL_STORY } from './utils/featureToggle/const'
import snowplowValueWithLength from './utils/snowplowValueWithLength'

if (isFeatureEnabled(FULL_STORY)) FullStoryAnalytics.init()

class App extends Component {
  constructor(props) {
    super(props)

    this.params = getRouteParams(window.location.pathname)
    this.query = parse(window.location.search)
    this.metaParams = getMetaParamsFromQuery(this.query)

    const {
      theme,
      hideTooltip,
      variation,
      customCTA,
      pageUrl,
      marketplaces,
      defaultFilters,
      zipCode,
      activeTab,
      loanType,
      terms,
      sorting,
      omitUtmParams,
      multiVerticalOptions,
      isJumbo,
      uniqueId,
      ...options
    } = this.query

    const { marketplace, widgetType } = this.params

    // Initialize the Cookies
    this.cookies = new Cookies()

    this.credclid = this.query.credclid || uuid()
    this.credsessid = this.query.credsessid || uuid()

    // Push Segemnt needed variables to GTM Data Layer
    populatedGtmDataLayer({
      ...this.metaParams,
      pageUrl,
    })

    // Initialize GTM service
    gtm.initialize(config.gtmContainerId, this.cookies)

    // Initialize the logger component
    createLogger(
      {
        app: 'partners-widgets',
        credclid: this.credclid,
        credsessid: this.credsessid,
      },
      { credibleRoot: config.credibleRoot },
    )

    this.state = {
      // Options provided by partner.
      // These fallback values we need for:
      // - test widget in QA environment when host page is missing;
      // - local development.
      // Once widget is embedded into partner site these options will be replaced
      // by partners specific.
      options: {
        ui: {
          theme,
          hideTooltip: [true, 'true'].includes(hideTooltip),
        },
        product: {
          type: widgetType,
          marketplace: getMarketplaceParam(marketplace, marketplaces),
          variation,
          customCTA,
        },
        meta: this.metaParams,
        multiVertical: parseMultiVerticalOptions(multiVerticalOptions),
        omitUtmParams,
        uniqueId,
      },
      isJumbo,
      activeTab: activeTab || DEFAULT_ACTIVE_TAB,
      initialValues: {
        zipCode,
        loanType,
        terms,
        sorting,
        options,
      },
      // Global Snowplow context provided by Credible SDK.
      parentPage: {
        partnerReferrer: snowplowValueWithLength(
          'partnerReferrer',
          pageUrl || document.referrer || 'undefined',
          2048,
        ),
        partnerDomain: extractHostname(document.referrer),
        partnerURL: pageUrl || document.referrer,
      },
      credclid: this.credclid,
      credsessid: this.credsessid,
      renderId: uuid(),
      source: this.query.source,
      campaignFromPageUrl: pageUrl,
    }
  }

  componentDidMount() {
    // Analytics initialization
    Snowplow.initialize()

    const {
      options: {
        product: { type },
      },
    } = this.state

    if (type === CALCULATOR_WITH_INSURANCE_FORM_TYPE) {
      setInsuranceFormInitialValues({ search: this.query })
    }
  }

  componentWillUnmount() {
    SDKClientCommunicatorInterface.sentMessage('onDestroy')
  }

  setOptionsSuppliedByPartner = ({ initWidgetOptions, spContext }) => {
    const { parentPage: parentPageDefault } = this.state

    const parentPageNewValues = removeEmptyValues(spContext)

    this.setState({
      options: initWidgetOptions,
      parentPage: { ...parentPageDefault, ...parentPageNewValues },
    })
  }

  initSDKClientLib = (data) => {
    SDKClientCommunicatorInterface.sentMessage('onInit')
    SDKClientCommunicatorInterface.sentMessage('scrollRequest', 'top')

    this.setOptionsSuppliedByPartner(data)
  }

  render() {
    const {
      options,
      parentPage,
      credclid,
      credsessid,
      renderId,
      source,
      campaignFromPageUrl,
      initialValues,
      isJumbo,
      activeTab,
    } = this.state
    const { preferred_lender, otherOptions: otherOptionsFromQuery } = this.query
    const otherOptions = getOtherOptionsParam(otherOptionsFromQuery)
    const { theme } = options.ui
    const { children } = this.props
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const widgetContext = {
      options,
      parentPage,
      credclid,
      credsessid,
      renderId,
      source,
      campaignFromPageUrl,
      partnersPreferredLender: preferred_lender,
      richCta: { ctaTitle: initialValues.options.ctaTitle },
      vertical: options.product.marketplace,
      otherOptions,
    }
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const initialValuesContext = {
      isJumbo,
      initialValues: pickby(initialValues, identity),
      activeTab,
    }

    return (
      <>
        <WidgetSDKClient
          logger={logger}
          appName="partners-widgets"
          onDataReceive={this.initSDKClientLib}
        />
        <div className={styles.container}>
          <ApplicationErrorBoundary>
            <CookiesProvider cookies={this.cookies}>
              <WidgetContext.Provider value={widgetContext}>
                <InitialValuesContext.Provider value={initialValuesContext}>
                  <ThemeProvider themeName={theme}>{children}</ThemeProvider>
                </InitialValuesContext.Provider>
              </WidgetContext.Provider>
            </CookiesProvider>
          </ApplicationErrorBoundary>
        </div>
      </>
    )
  }
}

App.propTypes = {
  children: PropTypes.node.isRequired,
}

export { parseMultiVerticalOptions }

export default scriptLoader(
  'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/3.6.6/iframeResizer.contentWindow.min.js',
)(App)
